const DEFAULT_THEME = {
  ring: 'rgba(var(--accent-rgb),.22)',
  shadow: '0 0 0 4px rgba(var(--accent-rgb),.06)',
  badgeBg: 'rgba(var(--accent-rgb),.16)',
  badgeBorder: 'rgba(var(--accent-rgb),.3)',
  badgeColor: 'var(--accent)',
}

const THEME_MAP = {
  WORKER: {
    PRO: {
      ring: 'rgba(205,127,50,.5)',
      shadow: '0 0 0 4px rgba(205,127,50,.1)',
      badgeBg: 'rgba(205,127,50,.16)',
      badgeBorder: 'rgba(205,127,50,.34)',
      badgeColor: '#d6a066',
    },
    VIP: {
      ring: 'rgba(245,194,66,.7)',
      shadow: '0 0 0 4px rgba(245,194,66,.12)',
      badgeBg: 'rgba(245,194,66,.16)',
      badgeBorder: 'rgba(245,194,66,.34)',
      badgeColor: '#f5c242',
    },
  },
  EMPLOYER: {
    VIP: {
      ring: 'rgba(203,213,225,.65)',
      shadow: '0 0 0 4px rgba(203,213,225,.1)',
      badgeBg: 'rgba(203,213,225,.14)',
      badgeBorder: 'rgba(203,213,225,.32)',
      badgeColor: '#d8dee9',
    },
  },
}

import { fmtDate } from '../utils/date'

export function formatSubscriptionDate(value) {
  return fmtDate(value) || 'Yoxdur'
}

export function getSubscriptionTheme(subscription) {
  if (!subscription?.planType || !subscription?.roleType) {
    return DEFAULT_THEME
  }
  return THEME_MAP[subscription.roleType]?.[subscription.planType] || DEFAULT_THEME
}

export function getSubscriptionLabel(subscription) {
  if (!subscription?.planType) return null
  if (subscription.roleType === 'EMPLOYER' && (subscription.planType === 'DEFAULT' || subscription.planType === 'FREE')) return 'Free'
  if (subscription.planType === 'DEFAULT') return 'Default'
  return subscription.planType
}

export function getSubscriptionMeta(subscription) {
  if (!subscription?.planType || !subscription?.roleType) {
    return null
  }

  if (subscription.roleType === 'WORKER' && subscription.planType === 'PRO') {
    return { text: 'PRO worker', short: 'PRO' }
  }

  if (subscription.roleType === 'WORKER' && subscription.planType === 'VIP') {
    return { text: 'VIP worker', short: 'VIP' }
  }

  if (subscription.roleType === 'EMPLOYER' && subscription.planType === 'VIP') {
    return { text: 'VIP employer', short: 'VIP' }
  }

  if (subscription.roleType === 'WORKER') {
    return { text: 'Free worker', short: 'FREE' }
  }

  return { text: 'Free employer', short: 'FREE' }
}

export function SubscriptionPlanPill({ subscription, compact = false, roleLabel }) {
  const meta = getSubscriptionMeta(subscription)
  if (!meta) return null

  const theme = getSubscriptionTheme(subscription)
  const content = compact ? meta.short : roleLabel || meta.text

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '.35rem',
        padding: compact ? '.14rem .48rem' : '.2rem .65rem',
        borderRadius: '999px',
        border: `1px solid ${theme.badgeBorder}`,
        background: theme.badgeBg,
        color: theme.badgeColor,
        fontSize: compact ? '.64rem' : '.7rem',
        fontWeight: 700,
        letterSpacing: '.03em',
        whiteSpace: 'nowrap',
      }}
      title={meta.text}
    >
      {content}
    </span>
  )
}

export function SubscriptionAvatar({
  subscription,
  name,
  photoUrl,
  size = 80,
  verified = false,
}) {
  const theme = getSubscriptionTheme(subscription)
  const meta = getSubscriptionMeta(subscription)
  const badgeSize = Math.max(22, Math.round(size * 0.3))
  const verificationSize = Math.max(18, Math.round(size * 0.24))

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      {photoUrl ? (
        <img loading="lazy"
          src={photoUrl}
          alt="profil"
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            objectFit: 'cover',
            border: `3px solid ${theme.ring}`,
            boxShadow: theme.shadow,
          }}
        />
      ) : (
        <div
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: 'rgba(var(--accent-rgb),.1)',
            border: `3px solid ${theme.ring}`,
            boxShadow: theme.shadow,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: Math.max(22, Math.round(size * 0.34)),
            fontWeight: 800,
            color: 'var(--accent)',
          }}
        >
          {name?.charAt(0).toUpperCase()}
        </div>
      )}

      {verified && (
        <span
          style={{
            position: 'absolute',
            top: -2,
            right: -2,
            width: verificationSize,
            height: verificationSize,
            borderRadius: '50%',
            background: '#1d9bf0',
            border: '2px solid var(--bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: Math.max(10, Math.round(size * 0.12)),
            fontWeight: 800,
          }}
          title="Təsdiqlənmiş hesab"
        >
          ✓
        </span>
      )}

      {meta && (subscription?.vip || subscription?.pro) && (
        <span
          style={{
            position: 'absolute',
            right: -4,
            bottom: -4,
            minWidth: badgeSize,
            height: badgeSize,
            padding: '0 .35rem',
            borderRadius: '999px',
            background: theme.badgeBg,
            color: theme.badgeColor,
            border: `1px solid ${theme.badgeBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: Math.max(10, Math.round(size * 0.11)),
            fontWeight: 800,
            boxShadow: '0 10px 24px rgba(0,0,0,.22)',
            backdropFilter: 'blur(8px)',
          }}
          title={meta.text}
        >
          {meta.short}
        </span>
      )}
    </div>
  )
}

export function SubscriptionInfoPanel({ title, subscription, roleLabel }) {
  const meta = getSubscriptionMeta(subscription)
  const theme = getSubscriptionTheme(subscription)
  const applicationValue = subscription?.roleType === 'WORKER'
    ? (subscription?.unlimitedApplications ? 'Limitsiz' : (subscription?.coinBalance ?? '0'))
    : 'Yoxdur'

  return (
    <div
      style={{
        padding: '1rem 1.1rem',
        borderRadius: 'var(--radius-sm)',
        background: 'rgba(255,255,255,.03)',
        border: `1px solid ${theme.badgeBorder}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '.75rem',
          marginBottom: '.9rem',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{ fontSize: '.78rem', color: 'var(--muted)', marginBottom: '.15rem' }}>{title}</div>
          <div style={{ fontSize: '.95rem', fontWeight: 700 }}>{getSubscriptionLabel(subscription) || 'Yoxdur'}</div>
        </div>
        <SubscriptionPlanPill subscription={subscription} roleLabel={roleLabel || meta?.text} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.7rem' }}>
        <InfoItem label="Müraciət" value={applicationValue} />
        <InfoItem label="Başlayıb" value={formatSubscriptionDate(subscription?.startsAt)} />
        <InfoItem label="Bitəcək" value={formatSubscriptionDate(subscription?.expiresAt)} />
        <InfoItem label="Sıfırlanma" value={formatSubscriptionDate(subscription?.resetAt)} />
      </div>

      {subscription?.benefits?.length > 0 && (
        <div style={{ marginTop: '.95rem', display: 'flex', flexDirection: 'column', gap: '.45rem' }}>
          {subscription.benefits.map(item => (
            <div key={item} style={{ fontSize: '.76rem', color: 'var(--muted)', lineHeight: 1.5 }}>
              • {item}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function InfoItem({ label, value }) {
  return (
    <div
      style={{
        padding: '.65rem .75rem',
        borderRadius: 'var(--radius-sm)',
        background: 'rgba(255,255,255,.02)',
        border: '1px solid rgba(255,255,255,.06)',
      }}
    >
      <div style={{ fontSize: '.72rem', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
        {label}
      </div>
      <div style={{ fontSize: '.82rem', fontWeight: 600, marginTop: '.25rem' }}>{value}</div>
    </div>
  )
}
